<script lang="ts" setup>
import type { DisplayableOfferProperty, OfferImage, OfferMedium, OfferSmall } from '~/types/Catalog';

const props = defineProps<{ offer: OfferMedium; recommendationsBlockId?: string }>();

const images = ref<OfferImage[]>(
  [...props.offer.offer_images, ...props.offer.product_images]
    .sort((a, b) => Number(b.is_main) - Number(a.is_main))
    .slice(0, 6),
);

async function goToReviews() {
  await navigateTo({ path: props.offer.uri, query: { tab: 'reviews' }, hash: '#feedback' });
}

function getOfferColor(offer: OfferSmall | OfferMedium): string {
  return offer.displayable_offer_properties.find((p) => p.name === 'Цвет')?.value ?? '';
}

function sortSizeOffers(offer: OfferMedium) {
  const { size_charts_values, offers, size_charts } = offer;

  if (!size_charts_values || size_charts_values.length === 0) return offers;

  const sizeChartId = size_charts_values[0].size_chart_id;
  const chart = size_charts.find((sizeChart) => sizeChart.id === sizeChartId);
  const sizeChartValues = chart ? chart.size_chart_values : [];

  return offers
    .map((offer) => {
      const sizeProp = offer.displayable_offer_properties?.find((p: DisplayableOfferProperty) =>
        p.name.includes('Размер'),
      );

      return {
        ...offer,
        order:
          sizeChartValues.find((sizeChartValue) => {
            return offer.sizes?.[0].value === sizeChartValue.title || sizeProp?.value === sizeChartValue.title;
          })?.order ?? undefined,
        $isDisabled: offer.balance !== undefined && offer.balance < 1,
      };
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

const sizes = computed(() => {
  const currentOfferColor = getOfferColor(props.offer);

  if (currentOfferColor) {
    const sortedSizeOffers = sortSizeOffers(props.offer);

    return sortedSizeOffers
      .filter((offer) => getOfferColor(offer) === currentOfferColor)
      .map((o) => {
        const sizeProp = o.displayable_offer_properties.find((p) => p.name === 'Размер');
        return sizeProp?.value;
      })
      .join(' ');
  }
  return '';
});

const { accessRights } = storeToRefs(useAuthStore());
</script>

<template>
  <CardBlock class="product-card">
    <div class="product-card__image">
      <ProductCardImage
        :alt="offer.product_name"
        :images
        class="pt-5"
      />

      <BadgesAndFlagsBlock
        :offer
        class="product-card__badges-block"
        used-in="category"
      />

      <FavoriteBtn
        :offer
        class="product-card__favorite-btn"
      />
    </div>

    <section class="product-card__info">
      <div class="flex justify-between flex-wrap text-sm">
        <AvailabilityIndicator :offer />

        <button
          v-if="Number(offer.rating) > 0"
          class="flex items-center gap-1 transition hover:underline"
          @click.stop.prevent="goToReviews"
        >
          <SvgoStar
            class="text-hell-black text-xs"
            filled
          />
          {{ Number(offer.rating).toFixed(1) }}
        </button>
      </div>

      <div class="flex-grow transition leading-5">
        <template v-if="accessRights?.show_colors_at_index">
          {{ offer.product_name_medium ?? offer.product_name }}
        </template>

        <template v-else>
          {{ offer.product_name_without_properties ?? offer.product_name_medium ?? offer.product_name }}
        </template>

        <div
          v-if="sizes"
          class="product-card__sizes"
        >
          {{ sizes }}
        </div>
      </div>

      <ProductCardFooter
        :offer
        :recommendations-block-id="recommendationsBlockId"
      />

      <AdminOfferAvailabilityBlock :offer />
    </section>
  </CardBlock>
</template>

<style lang="scss" scoped>
.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow: hidden;
  height: 100%;
  border: 1px solid transparent;
  font-size: $text-sm;
  transition: border-color 0.3s;

  @include screen-sm {
    gap: 16px;
  }

  &:hover {
    border-color: $hell-black-10;

    a {
      color: $hell-black;
    }

    @include screen-lg {
      .product-card-footer {
        border-color: $hell-black;
        background-color: $hell-black;
        color: $white;
      }
    }
  }

  &__image {
    position: relative;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    font-size: $text-sm;

    @include screen-md {
      font-size: $text-base;
    }
  }

  &__sizes {
    overflow: hidden;
    margin-top: 4px;
    color: $hell-black-40;
    font-size: $text-xs;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__badges-block {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 11;
  }

  &__favorite-btn {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 11;
  }
}
</style>
